import { useState } from "react";
import { App, Avatar, Dropdown, Modal, QRCode } from "antd";
import {
  ApartmentOutlined,
  BellOutlined,
  CheckOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  GlobalOutlined,
  LogoutOutlined,
  MoonOutlined,
  QrcodeOutlined,
  RightOutlined,
  SunOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { switchToNonPersonal } from "../store/authSlice";
import { setThemeMode } from "../store/themeSlice";
import { useUpdateUserConfiguration } from "../hooks/useConfiguration";
import type { ThemeMode } from "../lib/theme";
import { logout as logoutApi } from "../api/auth";
import { clearEmployeeRecord, getAccountIsPersonal } from "../lib/auth";
import { useFullscreen } from "./profileActions";
import { useOrganization } from "../hooks/useOrganization";
import { usePushFeatureEnabled } from "../hooks/usePushSubscription";
import PushSettingsModal from "./push/PushSettingsModal";
import { modalStyles, modalWrapStyle } from "../utils/modalStyles";

// Списъкът с езици на едно място — нов език = нов ред тук (+ преводите му).
const LANGUAGES = [
  { key: "bg", flag: "🇧🇬", label: "Български" },
  { key: "en", flag: "🇬🇧", label: "English" },
];

const R = {
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  text: "var(--color-ink)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  brand: "var(--color-brand-ink)",
  brandTint: "var(--color-brand-tint)",
};

interface ProfileDropdownProps {
  displayName: string;
  username: string;
  access: boolean;
  isNarrow: boolean;
  onLogout: () => void;
  /** Label за бутона за излизане когато access=false. По подразбиране common.logout. */
  logoutLabelKey?: string;
  /** Label за бутона когато access=true (отказване на достъп). По подразбиране modules.revokeAccess. */
  revokeAccessLabelKey?: string;
  /** Компактен режим (свит sidebar): само аватарът с менюто — без
      Backoffice/QR/име. */
  compact?: boolean;
}

export default function ProfileDropdown({
  displayName,
  username,
  access,
  isNarrow,
  onLogout,
  logoutLabelKey = "common.logout",
  revokeAccessLabelKey = "modules.revokeAccess",
  compact = false,
}: ProfileDropdownProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUserPersonal = useAppSelector((s) => s.auth.personal);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  // Редът „Известия" се крие изцяло, когато BE няма VAPID ключове.
  const pushFeatureEnabled = usePushFeatureEnabled();
  // „Цял екран" е самостоятелна икона в лентата (QR кодът е в менюто).
  const fs = useFullscreen();

  // Общ терминал с влязъл служител: „Изход" връща към пин-екрана (смяна на
  // служител), а НЕ пълен logout към /login. Пълният изход на устройството
  // остава поведение на личния акаунт (или на споделения без влязъл служител).
  const isSharedEmployee =
    getAccountIsPersonal() === false && currentUserPersonal === true;

  // Бутон „Backoffice" — само за акаунти с organization_access и не на телефон.
  const orgAccess = useAppSelector(
    (s) => s.auth.user?.organization_access === true,
  );
  const { data: organization } = useOrganization(orgAccess);
  const backofficeUrl =
    orgAccess && organization?.name
      ? `https://${organization.name}.unrealsoft.cloud`
      : null;

  const handleBackToPin = async () => {
    // Guard срещу двоен клик: втори logout след switchToNonPersonal би деактивирал
    // самата споделена сесия (освободил лиценза) вместо да върне към пин.
    if (busy) return;
    setBusy(true);
    try {
      await logoutApi(); // връща session_token на споделения акаунт (един лиценз)
    } catch (e) {
      console.error("Logout error:", e);
    }
    // Човешки жест — служителят си тръгва, затова безусловно. Съседните
    // прозорци виждат триенето през storage event и също падат до ПИН.
    clearEmployeeRecord();
    dispatch(switchToNonPersonal());
    navigate("/", { replace: true });
  };

  // При потвърден достъп (access) бутонът отказва достъпа през подадения onLogout;
  // иначе при общ терминал със служител връщаме към пин, инак — подадения logout.
  const effectiveAction =
    !access && isSharedEmployee ? handleBackToPin : onLogout;

  return (
    <>
      {/* Backoffice — линк към организацията; скрит на телефон и без
          organization_access. */}
      {backofficeUrl && !isNarrow && !compact && (
        <a
          href={backofficeUrl}
          target="_blank"
          rel="noreferrer"
          style={{
            height: 30,
            padding: "0 8px",
            borderRadius: 6,
            background: "transparent",
            border: "none",
            color: R.textMuted,
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12.5,
            fontWeight: 600,
            lineHeight: 1,
            textDecoration: "none",
            whiteSpace: "nowrap",
            flexShrink: 0,
            transition: "background 120ms, color 120ms",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--color-shade-1)";
            e.currentTarget.style.color = R.ink;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = R.textMuted;
          }}
        >
          <ApartmentOutlined style={{ fontSize: 15 }} />
          {t("common.backoffice")}
        </a>
      )}

      {/* Цял екран — самостоятелна икона вън от Dropdown trigger-а, за да не
          отваря менюто (QR кодът е ред В менюто). */}
      {!compact && (
      <button
        type="button"
        title={
          fs.isFullscreen ? t("common.exitFullscreen") : t("common.fullscreen")
        }
        onClick={fs.toggle}
        style={{
          height: 30,
          width: 30,
          borderRadius: 6,
          background: "transparent",
          border: "none",
          color: R.textMuted,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "background 120ms, color 120ms",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "var(--color-shade-1)";
          e.currentTarget.style.color = R.ink;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = R.textMuted;
        }}
      >
        {fs.isFullscreen ? (
          <FullscreenExitOutlined style={{ fontSize: 15 }} />
        ) : (
          <FullscreenOutlined style={{ fontSize: 15 }} />
        )}
      </button>
      )}

      <Dropdown
        trigger={["click"]}
        placement="bottomRight"
        open={open}
        onOpenChange={setOpen}
        dropdownRender={() => (
          <ProfileMenu
            displayName={displayName}
            username={username}
            access={access}
            onLogout={() => {
              setOpen(false);
              effectiveAction();
            }}
            onOpenTheme={() => {
              setOpen(false);
              setThemeOpen(true);
            }}
            onOpenLanguage={() => {
              setOpen(false);
              setLangOpen(true);
            }}
            onOpenQr={() => {
              setOpen(false);
              setQrOpen(true);
            }}
            showPush={pushFeatureEnabled}
            onOpenPush={() => {
              setOpen(false);
              setPushOpen(true);
            }}
            logoutLabelKey={logoutLabelKey}
            revokeAccessLabelKey={revokeAccessLabelKey}
          />
        )}
      >
        <div
          className="flex items-center cursor-pointer"
          style={{ gap: 8 }}
        >
          {displayName && !compact && (
            <span
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: R.ink,
                lineHeight: 1.2,
                // На телефон името остава, но клампнато, за да не измества лентата.
                maxWidth: isNarrow ? 110 : 200,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {displayName}
            </span>
          )}
          <Avatar
            size={28}
            style={{ background: "#D97706" }}
            icon={<UserOutlined />}
          />
        </div>
      </Dropdown>

      <ThemeModal open={themeOpen} onClose={() => setThemeOpen(false)} />
      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} />
      <QrModal open={qrOpen} onClose={() => setQrOpen(false)} />
      <PushSettingsModal open={pushOpen} onClose={() => setPushOpen(false)} />
    </>
  );
}

export function QrModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Базовият адрес на ресторанта — без пътя на текущия модул.
  const url = window.location.origin;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnHidden
      width={340}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div
        style={{
          padding: "18px 20px 14px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: R.brandTint,
            color: R.brand,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <QrcodeOutlined style={{ fontSize: 16 }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: R.ink,
              letterSpacing: -0.2,
            }}
          >
            {t("common.qrTitle")}
          </div>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 500,
              color: R.textMuted,
              marginTop: 2,
            }}
          >
            {t("common.qrSubtitle")}
          </div>
        </div>
      </div>

      <div style={{ height: 1, background: R.border }} />

      <div
        style={{
          padding: "20px 20px 18px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
        }}
      >
        <QRCode
          value={url}
          size={216}
          color="#1A1A1A"
          bgColor="#FFFFFF"
          bordered={false}
        />
        <div
          style={{
            fontFamily: '"JetBrains Mono", ui-monospace, monospace',
            fontSize: 11,
            fontWeight: 500,
            color: R.textMuted,
            letterSpacing: 0.3,
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {url}
        </div>
      </div>
    </Modal>
  );
}

export function ThemeModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { message } = App.useApp();
  const currentTheme = useAppSelector((state) => state.theme.mode);
  const { mutate: updateConfiguration } = useUpdateUserConfiguration();

  // Списъкът с теми на едно място — по модела на LANGUAGES.
  const THEMES: { key: ThemeMode; icon: React.ReactNode; label: string }[] = [
    {
      key: "light",
      icon: <SunOutlined style={{ fontSize: 15 }} />,
      label: t("common.themeLight"),
    },
    {
      key: "dark",
      icon: <MoonOutlined style={{ fontSize: 15 }} />,
      label: t("common.themeDark"),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={340}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div
        style={{
          padding: "18px 20px 14px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: R.brandTint,
            color: R.brand,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <MoonOutlined style={{ fontSize: 16 }} />
        </div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: R.ink,
            letterSpacing: -0.2,
          }}
        >
          {t("common.chooseTheme")}
        </div>
      </div>

      <div style={{ height: 1, background: R.border }} />

      <div style={{ padding: 10 }}>
        {THEMES.map((themeOption) => (
          <MenuRow
            key={themeOption.key}
            icon={themeOption.icon}
            label={themeOption.label}
            active={currentTheme === themeOption.key}
            onClick={() => {
              // Прилагаме веднага (без да чакаме мрежата) и записваме в
              // личните настройки; при грешка връщаме предишната тема.
              const previous = currentTheme;
              dispatch(setThemeMode(themeOption.key));
              onClose();
              if (themeOption.key === previous) return;
              updateConfiguration(
                { theme: themeOption.key },
                {
                  onError: () => {
                    dispatch(setThemeMode(previous));
                    message.error(t("orderingSettings.saveError"));
                  },
                },
              );
            }}
          />
        ))}
      </div>
    </Modal>
  );
}

export function LanguageModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith("bg") ? "bg" : "en";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={340}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div
        style={{
          padding: "18px 20px 14px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: R.brandTint,
            color: R.brand,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <GlobalOutlined style={{ fontSize: 16 }} />
        </div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: R.ink,
            letterSpacing: -0.2,
          }}
        >
          {t("common.chooseLanguage")}
        </div>
      </div>

      <div style={{ height: 1, background: R.border }} />

      <div style={{ padding: 10 }}>
        {LANGUAGES.map((lang) => (
          <MenuRow
            key={lang.key}
            icon={<span style={{ fontSize: 15, lineHeight: 1 }}>{lang.flag}</span>}
            label={lang.label}
            active={currentLang === lang.key}
            onClick={() => {
              i18n.changeLanguage(lang.key);
              onClose();
            }}
          />
        ))}
      </div>
    </Modal>
  );
}

function ProfileMenu({
  displayName,
  username,
  access,
  onLogout,
  onOpenTheme,
  onOpenLanguage,
  onOpenQr,
  showPush,
  onOpenPush,
  logoutLabelKey,
  revokeAccessLabelKey,
}: {
  displayName: string;
  username: string;
  access: boolean;
  onLogout: () => void;
  onOpenTheme: () => void;
  onOpenLanguage: () => void;
  onOpenQr: () => void;
  showPush: boolean;
  onOpenPush: () => void;
  logoutLabelKey: string;
  revokeAccessLabelKey: string;
}) {
  const { t, i18n } = useTranslation();
  const initial =
    (displayName?.trim()?.[0] || username?.trim()?.[0] || "?").toUpperCase();

  const currentLang = i18n.language?.startsWith("bg") ? "bg" : "en";
  const currentLangLabel =
    LANGUAGES.find((l) => l.key === currentLang)?.label ?? currentLang;

  const currentTheme = useAppSelector((state) => state.theme.mode);
  const currentThemeLabel =
    currentTheme === "dark" ? t("common.themeDark") : t("common.themeLight");

  return (
    <div
      style={{
        width: 260,
        borderRadius: 14,
        background: R.surface,
        border: `1px solid ${R.border}`,
        boxShadow:
          "0 12px 32px rgba(17,17,17,0.10), 0 2px 8px rgba(17,17,17,0.05)",
        overflow: "hidden",
        fontFamily: "inherit",
      }}
    >
      {/* Identity */}
      <div
        style={{
          padding: "16px 16px 14px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          background: access
            ? `radial-gradient(ellipse 90% 120% at 100% 0%, ${R.brandTint} 0%, transparent 70%), ${R.surface}`
            : R.surface,
        }}
      >
        <div
          style={{
            position: "relative",
            width: 40,
            height: 40,
            borderRadius: 10,
            background: "#D97706",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: -0.3,
            boxShadow: "0 2px 8px rgba(217,119,6,0.25)",
            flexShrink: 0,
          }}
        >
          {initial}
          {access && (
            <span
              title={t("modules.accessOn")}
              style={{
                position: "absolute",
                bottom: -2,
                right: -2,
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: "#D97706",
                border: `2px solid ${R.surface}`,
                boxShadow: "0 0 0 2px rgba(217,119,6,0.25)",
              }}
            />
          )}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: R.ink,
              letterSpacing: -0.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayName || username || "—"}
          </div>
          {username && displayName && username !== displayName && (
            <div
              style={{
                fontSize: 11.5,
                color: R.textMuted,
                marginTop: 2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              @{username}
            </div>
          )}
        </div>
      </div>

      <div style={{ height: 1, background: R.border }} />

      {/* Тема + Език + QR код */}
      <div style={{ padding: 6 }}>
        <MenuRow
          icon={
            currentTheme === "dark" ? (
              <MoonOutlined style={{ fontSize: 14 }} />
            ) : (
              <SunOutlined style={{ fontSize: 14 }} />
            )
          }
          label={t("common.theme")}
          trailing={
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                fontWeight: 600,
                color: R.textMuted,
              }}
            >
              {currentThemeLabel}
              <RightOutlined style={{ fontSize: 10 }} />
            </span>
          }
          onClick={onOpenTheme}
        />
        <MenuRow
          icon={<GlobalOutlined style={{ fontSize: 14 }} />}
          label={t("common.language")}
          trailing={
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                fontWeight: 600,
                color: R.textMuted,
              }}
            >
              {currentLangLabel}
              <RightOutlined style={{ fontSize: 10 }} />
            </span>
          }
          onClick={onOpenLanguage}
        />
        <MenuRow
          icon={<QrcodeOutlined style={{ fontSize: 14 }} />}
          label={t("common.qrTitle")}
          onClick={onOpenQr}
        />
        {showPush && (
          <MenuRow
            icon={<BellOutlined style={{ fontSize: 14 }} />}
            label={t("push.menuLabel")}
            onClick={onOpenPush}
          />
        )}
      </div>

      <div style={{ height: 1, background: R.border }} />

      {/* Изход */}
      <div style={{ padding: 6 }}>
        <MenuRow
          icon={<LogoutOutlined style={{ fontSize: 14 }} />}
          label={access ? t(revokeAccessLabelKey) : t(logoutLabelKey)}
          danger
          onClick={onLogout}
        />
      </div>
    </div>
  );
}

function MenuRow({
  icon,
  label,
  onClick,
  active,
  danger,
  trailing,
}: {
  icon: React.ReactNode;
  label: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  danger?: boolean;
  trailing?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        borderRadius: 8,
        border: "none",
        background: active ? R.brandTint : "transparent",
        color: active ? R.brand : R.text,
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: -0.1,
        cursor: "pointer",
        textAlign: "left",
        transition: "background 120ms, color 120ms",
      }}
      onMouseEnter={(e) => {
        if (active) return;
        if (danger) {
          e.currentTarget.style.background = "var(--color-danger-tint)";
          e.currentTarget.style.color = "var(--color-danger-ink)";
        } else {
          e.currentTarget.style.background = "var(--color-shade-1)";
        }
      }}
      onMouseLeave={(e) => {
        if (active) return;
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = R.text;
      }}
    >
      <span
        style={{
          width: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
      {trailing}
      {active && <CheckOutlined style={{ fontSize: 12, color: R.brand }} />}
    </button>
  );
}
